"use client";

import type React from "react";

import { Eye, EyeOff } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PasswordRule } from "~/api/model/passwordRule";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { cn } from "~/lib/utils";

// --- Rule Definitions ---
interface RuleDefinition {
  id: string;
  text: string;
  validate: (password: string) => boolean;
  score: number;
}

const ALL_POSSIBLE_RULES: RuleDefinition[] = [
  {
    id: "require_length",
    text: "Long Enough",
    validate: (p) => p.length >= 8,
    score: 20,
  },
  {
    id: "require_lowercase",
    text: "Lowercase",
    validate: (p) => /[a-z]/.test(p),
    score: 15,
  },
  {
    id: "require_uppercase",
    text: "Uppercase",
    validate: (p) => /[A-Z]/.test(p),
    score: 20,
  },
  {
    id: "require_numbers",
    text: "Number",
    validate: (p) => /[0-9]/.test(p),
    score: 20,
  },
  {
    id: "require_symbols",
    text: "Special Character",
    validate: (p) => /[^A-Za-z0-9]/.test(p),
    score: 25,
  },
];

// --- Password Strength Logic ---
interface PasswordStrength {
  score: number;
  level: "none" | "weak" | "fair" | "good" | "strong" | "excellent";
  feedback: string;
  metRules: Record<string, boolean>;
}

const calculatePasswordStrength = (
  password: string,
  activeRules: RuleDefinition[]
): PasswordStrength => {
  let score = 0;
  const metRules: Record<string, boolean> = {};
  let maxPossibleScore = 0;

  activeRules.forEach((rule) => {
    maxPossibleScore += rule.score;
    if (rule.validate(password)) {
      score += rule.score;
      metRules[rule.id] = true;
    } else {
      metRules[rule.id] = false;
    }
  });

  if (password.length === 0) {
    return {
      score: 0,
      level: "none",
      feedback: "Enter a password to begin.",
      metRules,
    };
  }

  const percentageScore =
    maxPossibleScore > 0 ? (score / maxPossibleScore) * 100 : 0;

  if (percentageScore < 30)
    return { score, level: "weak", feedback: "Very weak password.", metRules };
  if (percentageScore < 50)
    return { score, level: "fair", feedback: "Could be stronger.", metRules };
  if (percentageScore < 70)
    return {
      score,
      level: "good",
      feedback: "This is a good password.",
      metRules,
    };
  if (percentageScore < 90)
    return {
      score,
      level: "strong",
      feedback: "This is a strong password.",
      metRules,
    };
  return {
    score,
    level: "excellent",
    feedback: "Excellent! This is a very strong password.",
    metRules,
  };
};

// --- UI Components ---

// --- UI Components ---

const ConfettiPiece = ({ style }: { style: React.CSSProperties }) => (
  <div
    className="absolute z-5" // Removed rounded-full
    style={style} // The style object will now contain width, height, and all animation properties
  />
);

const Confetti = () => {
  const confettiCount = 25; // Increased particle count
  const animationDuration = 0.5; // Increased animation duration (seconds)

  const pieces = useMemo(() => {
    return Array.from({ length: confettiCount }).map((_, i) => {
      const angleRad = ((Math.random() - 0.5) * 180 * Math.PI) / 180; // Spread: -60 to +60 deg from vertical
      const horizontalForce = 60 + Math.random() * 35;
      const upwardForce = 20 + Math.random() * 40; // Slightly increased upward force
      const gravityEffect = 60 + Math.random() * 30; // Slightly increased gravity effect for longer travel

      const xEnd = Math.sin(angleRad) * (horizontalForce + upwardForce * 0.4);
      const yApex = upwardForce;
      const yLand = gravityEffect;

      const colors = [
        "#f44336",
        "#e91e63",
        "#9c27b0",
        "#673ab7",
        "#2196f3",
        "#4caf50",
        "#ffc107",
        "#ff5722",
      ];

      const pieceWidth = 1 + Math.random() * 2;
      const pieceHeight = 1 + Math.random() * 5;

      const initialRotation = Math.random() * 360; // degrees
      // Total rotation amount during animation (1 to 2.5 full spins, random direction)
      const rotationAmount =
        (Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 540);

      const style: React.CSSProperties = {
        backgroundColor: colors[i % colors.length],
        top: "50%",
        left: "50%",
        width: `${pieceWidth}px`,
        height: `${pieceHeight}px`,
        opacity: 0.5, // Will be handled by animation
        transform: `translate(-50%, -50%) rotate(${initialRotation}deg)`, // Initial state for animation
        position: "absolute", // Ensure position is absolute for transform origin to work as expected from center
        // CSS Variables for the animation
        ["--x-end" as string]: `${xEnd}px`,
        ["--y-apex" as string]: `${yApex}px`,
        ["--y-land" as string]: `${yLand}px`,
        ["--initial-rotate" as string]: `${initialRotation}deg`,
        ["--rotate-amount" as string]: `${rotationAmount}deg`,
        animationName: "confetti-burst",
        animationDuration: `${animationDuration}s`,
        animationTimingFunction: "ease-out", // Keeps the deceleration
        animationFillMode: "forwards",
        animationDelay: `${Math.random() * (animationDuration / 1)}s`, // Staggered start
      };
      return <ConfettiPiece key={i} style={style} />;
    });
  }, []);

  return (
    <div
      className="absolute z-0 inset-0 pointer-events-none"
      aria-hidden="true"
    >
      {" "}
      {/* Changed z-index from 1 to 0 */}
      {pieces}
    </div>
  );
};

const StrengthMeter = ({ level }: { level: PasswordStrength["level"] }) => {
  const levels = ["weak", "fair", "good", "strong", "excellent"];
  const currentLevelIndex = levels.indexOf(level);

  const levelConfig = {
    weak: "from-red-500 to-red-400",
    fair: "from-orange-500 to-orange-400",
    good: "from-yellow-500 to-yellow-400",
    strong: "from-green-500 to-green-400",
    excellent: "from-green-600 to-green-500",
    none: "bg-gray-200 dark:bg-gray-700",
  };

  return (
    <div className="flex items-center gap-1.5">
      {levels.map((lvl, index) => (
        <div
          key={lvl}
          className={cn(
            "h-2 w-full rounded-full transition-colors duration-300",
            index <= currentLevelIndex
              ? `bg-gradient-to-l ${levelConfig[level as keyof typeof levelConfig]}`
              : levelConfig.none
          )}
        />
      ))}
    </div>
  );
};

const Requirement = ({
  met,
  children,
  level,
}: {
  met: boolean;
  children: React.ReactNode;
  level: PasswordStrength["level"];
}) => {
  const unmetTintClass = {
    weak: "bg-red-500/20",
    fair: "bg-orange-500/20",
    good: "bg-yellow-500/20",
    strong: "bg-green-500/20",
    excellent: "bg-green-500/20",
    none: "bg-gray-300 dark:bg-gray-600",
  };

  return (
    <div className="flex items-center gap-1 transition-colors duration-300">
      <div
        className={cn(
          "h-3 w-3 rounded-full transition-all duration-300 flex-shrink-0",
          met ? "bg-green-500 scale-110" : unmetTintClass[level]
        )}
      />
      <span
        className={cn(
          "text-xs",
          met ? "text-base-foreground" : "text-muted-foreground"
        )}
      >
        {children}
      </span>
    </div>
  );
};

type TickleLevel = 0 | 1 | 2 | 3;

interface SecurityGuardCharacterProps {
  level: PasswordStrength["level"];
  tickleLevel: TickleLevel;
  setTickleLevel: React.Dispatch<React.SetStateAction<TickleLevel>>;
}

const SecurityGuardCharacter = ({
  level,
  tickleLevel,
  setTickleLevel,
}: SecurityGuardCharacterProps) => {
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [isBlinking, setIsBlinking] = useState(false);
  const [, setHeadTilt] = useState(0);
  const [mouthPathIndex, setMouthPathIndex] = useState(0);
  const [eyebrowOffset, setEyebrowOffset] = useState(0);
  const [mouthOffset, setMouthOffset] = useState(0);
  const svgRef = useRef<SVGSVGElement>(null);

  const prevTickleLevelRef = useRef<TickleLevel>(0);
  const [isSettlingDown, setIsSettlingDown] = useState(false);
  const [shake, setShake] = useState(0);

  const clickCountRef = useRef(0);
  const lastClickTimeRef = useRef(0);
  const clickResetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tickleEndTimerRef = useRef<NodeJS.Timeout | null>(null);
  const settleDownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const animationsPaused = tickleLevel > 0 || isSettlingDown;

  useEffect(() => {
    return () => {
      if (clickResetTimerRef.current) clearTimeout(clickResetTimerRef.current);
      if (tickleEndTimerRef.current) clearTimeout(tickleEndTimerRef.current);
      if (settleDownTimerRef.current) clearTimeout(settleDownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (animationsPaused) return;
    const blinkInterval = setInterval(
      () => {
        setIsBlinking(true);
        setTimeout(() => setIsBlinking(false), 150);
      },
      3000 + Math.random() * 2000
    );
    return () => clearInterval(blinkInterval);
  }, [animationsPaused]);

  useEffect(() => {
    if (animationsPaused) {
      setEyePosition({ x: 0, y: 0 });
      return;
    }
    const handleMouseMove = (event: MouseEvent) => {
      if (!svgRef.current || level === "none") {
        setEyePosition({ x: 0, y: 0 });
        return;
      }
      const { clientX, clientY } = event;
      const { left, top, width, height } =
        svgRef.current.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      const deltaX = clientX - centerX;
      const deltaY = clientY - centerY;
      const maxMove = 3;
      const moveX = Math.max(-maxMove, Math.min(maxMove, deltaX * 0.015));
      const moveY = Math.max(-maxMove, Math.min(maxMove, deltaY * 0.015));
      setEyePosition({ x: moveX, y: moveY });
    };
    if (level !== "none") window.addEventListener("mousemove", handleMouseMove);
    else setEyePosition({ x: 0, y: 0 });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [level, animationsPaused]);

  useEffect(() => {
    if (animationsPaused) {
      setHeadTilt(0);
      return;
    }
    const tiltAmount = 3;
    const intervalTime = 3500 + Math.random() * 3000;
    const headTiltInterval = setInterval(() => {
      const newTilt =
        (Math.random() > 0.5 ? 1 : -1) * (Math.random() * tiltAmount);
      setHeadTilt(newTilt);
      const holdTime = 700 + Math.random() * 800;
      setTimeout(() => {
        setHeadTilt(0);
      }, holdTime);
    }, intervalTime);
    return () => clearInterval(headTiltInterval);
  }, [animationsPaused]);

  useEffect(() => {
    if (animationsPaused) return;
    let mouthMoveInterval: NodeJS.Timeout | undefined;
    if (level === "none") {
      mouthMoveInterval = setInterval(
        () => {
          setMouthPathIndex((prev) => (prev + 1) % 3);
        },
        2000 + Math.random() * 1000
      );
    } else {
      setMouthPathIndex(0);
    }
    const eyebrowInterval = setInterval(
      () => {
        setEyebrowOffset((Math.random() - 0.5) * 0.5);
        setTimeout(() => setEyebrowOffset(0), 200);
      },
      4000 + Math.random() * 3000
    );
    const mouthInterval = setInterval(
      () => {
        setMouthOffset((Math.random() - 0.5) * 0.4);
        setTimeout(() => setMouthOffset(0), 200);
      },
      5000 + Math.random() * 3000
    );
    return () => {
      if (mouthMoveInterval) clearInterval(mouthMoveInterval);
      clearInterval(eyebrowInterval);
      clearInterval(mouthInterval);
    };
  }, [level, animationsPaused]);

  useEffect(() => {
    if (
      prevTickleLevelRef.current >= 2 &&
      tickleLevel === 0 &&
      !isSettlingDown
    ) {
      setIsSettlingDown(true);
      if (settleDownTimerRef.current) clearTimeout(settleDownTimerRef.current);
      settleDownTimerRef.current = setTimeout(() => {
        setIsSettlingDown(false);
      }, 700);
    }
    prevTickleLevelRef.current = tickleLevel;
  }, [tickleLevel, isSettlingDown]);

  const handleTickle = () => {
    if (tickleEndTimerRef.current) {
      clearTimeout(tickleEndTimerRef.current);
    }
    if (isSettlingDown) {
      setIsSettlingDown(false);
      if (settleDownTimerRef.current) clearTimeout(settleDownTimerRef.current);
    }
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    if (timeSinceLastClick < 400) {
      clickCountRef.current += 1;
    } else {
      clickCountRef.current = 1;
    }
    lastClickTimeRef.current = now;
    if (clickResetTimerRef.current) {
      clearTimeout(clickResetTimerRef.current);
    }
    clickResetTimerRef.current = setTimeout(() => {
      clickCountRef.current = 0;
    }, 500);
    let newTickleLevel: TickleLevel = 1;
    let duration = 800;
    if (clickCountRef.current >= 4) {
      newTickleLevel = 3;
      duration = 2500;
    } else if (clickCountRef.current >= 2) {
      newTickleLevel = 2;
      duration = 1500;
    }
    setTickleLevel(newTickleLevel);
    tickleEndTimerRef.current = setTimeout(() => {
      setTickleLevel(0);
      clickCountRef.current = 0;
    }, duration);
  };

  useEffect(() => {
    let shakeInterval: NodeJS.Timeout | undefined;
    let singleShakeTimeout: NodeJS.Timeout | undefined;
    if (tickleLevel > 0) {
      const rotationAmount = tickleLevel >= 2 ? 3.5 : 1.5;
      const intervalSpeed = tickleLevel >= 2 ? 60 : 90;
      shakeInterval = setInterval(() => {
        setShake((prev) =>
          prev === 0
            ? Math.random() > 0.5
              ? rotationAmount
              : -rotationAmount
            : 0
        );
      }, intervalSpeed);
    } else if (isSettlingDown) {
      setShake(Math.random() > 0.5 ? 1.5 : -1.5);
      singleShakeTimeout = setTimeout(() => {
        setShake(Math.random() > 0.5 ? 0.75 : -0.75);
        singleShakeTimeout = setTimeout(() => setShake(0), 200);
      }, 150);
    } else {
      setShake(0);
    }
    return () => {
      if (shakeInterval) clearInterval(shakeInterval);
      if (singleShakeTimeout) clearTimeout(singleShakeTimeout);
    };
  }, [tickleLevel, isSettlingDown]);

  const eyeBaseY = 20;
  const mouthBaseY = 29;
  const eyebrowBaseY = eyeBaseY - 5;
  const eyebrowFollowOffset =
    animationsPaused || level === "none" ? 0 : eyePosition.y * 0.4;
  const mouthFollowOffset =
    animationsPaused || level === "none" ? 0 : Math.max(0, eyePosition.y) * 0.3;

  const littleTickleAppearance = {
    mouth: `M 19 ${mouthBaseY} Q 24 ${mouthBaseY + 2.5} 29 ${mouthBaseY}`,
    eyebrows: {
      left: `M 15 ${eyebrowBaseY - 0.5} Q 18 ${eyebrowBaseY - 2} 21 ${eyebrowBaseY - 0.5}`,
      right: `M 27 ${eyebrowBaseY - 0.5} Q 30 ${eyebrowBaseY - 2} 33 ${eyebrowBaseY - 0.5}`,
    },
    eyesSquint: true,
  };
  const outrightLaughterAppearance = {
    mouth: `M 17 ${mouthBaseY - 1} C 17 ${mouthBaseY + 5}, 31 ${mouthBaseY + 5}, 31 ${mouthBaseY - 1} Z`,
    eyebrows: {
      left: `M 15 ${eyebrowBaseY - 1.5} Q 18 ${eyebrowBaseY - 3.5} 21 ${eyebrowBaseY - 1.5}`,
      right: `M 27 ${eyebrowBaseY - 1.5} Q 30 ${eyebrowBaseY - 3.5} 33 ${eyebrowBaseY - 1.5}`,
    },
    eyesClosedLaugh: true,
  };
  const verbalLaughterAppearance = {
    mouth: `M 16 ${mouthBaseY - 2} C 16 ${mouthBaseY + 6}, 32 ${mouthBaseY + 6}, 32 ${mouthBaseY - 2} Z`,
    eyebrows: {
      left: `M 14 ${eyebrowBaseY - 2} Q 18 ${eyebrowBaseY - 4.5} 22 ${eyebrowBaseY - 2}`,
      right: `M 26 ${eyebrowBaseY - 2} Q 30 ${eyebrowBaseY - 4.5} 34 ${eyebrowBaseY - 2}`,
    },
    eyesClosedLaugh: true,
  };
  const settleDownAppearance = {
    mouth: `M 19 ${mouthBaseY + 0.5} Q 24 ${mouthBaseY + 1.5} 29 ${mouthBaseY + 0.5}`,
    eyebrows: {
      left: `M 15 ${eyebrowBaseY + 0.5} L 21 ${eyebrowBaseY + 0.5}`,
      right: `M 27 ${eyebrowBaseY + 0.5} L 33 ${eyebrowBaseY + 0.5}`,
    },
  };

  const activeLevelConfig = {
    weak: {
      color: "text-red-500",
      fillColor: "fill-red-100 dark:fill-red-900",
      mouth: `M 18 ${mouthBaseY + 1} Q 24 ${mouthBaseY - 1} 30 ${mouthBaseY + 1}`,
      eyebrows: {
        left: `M 15 ${eyebrowBaseY + 1} L 21 ${eyebrowBaseY - 1}`,
        right: `M 27 ${eyebrowBaseY - 1} L 33 ${eyebrowBaseY + 1}`,
      },
    },
    fair: {
      color: "text-orange-500",
      fillColor: "fill-orange-100 dark:fill-orange-900",
      mouth: `M 18 ${mouthBaseY} L 30 ${mouthBaseY}`,
      eyebrows: {
        left: `M 15 ${eyebrowBaseY} L 21 ${eyebrowBaseY}`,
        right: `M 27 ${eyebrowBaseY} L 33 ${eyebrowBaseY}`,
      },
    },
    good: {
      color: "text-yellow-500",
      fillColor: "fill-yellow-100 dark:fill-yellow-900",
      mouth: `M 18 ${mouthBaseY - 1} Q 24 ${mouthBaseY + 2} 30 ${mouthBaseY - 1}`,
      eyebrows: {
        left: `M 15 ${eyebrowBaseY} Q 18 ${eyebrowBaseY - 1.5} 21 ${eyebrowBaseY}`,
        right: `M 27 ${eyebrowBaseY} Q 30 ${eyebrowBaseY - 1.5} 33 ${eyebrowBaseY}`,
      },
    },
    strong: {
      color: "text-green-400",
      fillColor: "fill-green-100 dark:fill-green-800",
      mouth: `M 18 ${mouthBaseY - 1} Q 24 ${mouthBaseY + 3} 30 ${mouthBaseY - 1}`,
      eyebrows: {
        left: `M 15 ${eyebrowBaseY} Q 18 ${eyebrowBaseY - 2} 21 ${eyebrowBaseY}`,
        right: `M 27 ${eyebrowBaseY} Q 30 ${eyebrowBaseY - 2} 33 ${eyebrowBaseY}`,
      },
    },
    excellent: {
      color: "text-green-600",
      fillColor: "fill-green-200 dark:fill-green-900",
      mouth: `M 19 ${mouthBaseY - 1} C 19 ${mouthBaseY + 3}, 29 ${mouthBaseY + 3}, 29 ${mouthBaseY - 1} Z`,
      eyebrows: {
        left: `M 15 ${eyebrowBaseY - 0.5} Q 18 ${eyebrowBaseY - 2.5} 21 ${eyebrowBaseY - 0.5}`,
        right: `M 27 ${eyebrowBaseY - 0.5} Q 30 ${eyebrowBaseY - 2.5} 33 ${eyebrowBaseY - 0.5}`,
      },
    },
  };
  const inactiveStateFeatures = {
    color: "text-gray-400 dark:text-gray-600",
    fillColor: "fill-gray-100 dark:fill-gray-800",
    mouth: [
      `M 18 ${mouthBaseY} L 30 ${mouthBaseY}`,
      `M 19 ${mouthBaseY - 0.5} Q 24 ${mouthBaseY + 0.5} 29 ${mouthBaseY - 0.5}`,
      `M 19 ${mouthBaseY + 0.5} Q 24 ${mouthBaseY - 0.5} 29 ${mouthBaseY + 0.5}`,
    ],
    eyebrows: {
      left: `M 15 ${eyebrowBaseY - 1} L 21 ${eyebrowBaseY - 1}`,
      right: `M 27 ${eyebrowBaseY - 1} L 33 ${eyebrowBaseY - 1}`,
    },
  };

  const baseConfig =
    level === "none"
      ? {
          ...inactiveStateFeatures,
          mouth: inactiveStateFeatures.mouth[mouthPathIndex],
        }
      : { ...activeLevelConfig[level] };

  const finalDisplayedConfig = { ...baseConfig };

  if (tickleLevel === 1) {
    finalDisplayedConfig.mouth = littleTickleAppearance.mouth;
    finalDisplayedConfig.eyebrows = littleTickleAppearance.eyebrows;
  } else if (tickleLevel === 2) {
    finalDisplayedConfig.mouth = outrightLaughterAppearance.mouth;
    finalDisplayedConfig.eyebrows = outrightLaughterAppearance.eyebrows;
  } else if (tickleLevel === 3) {
    finalDisplayedConfig.mouth = verbalLaughterAppearance.mouth;
    finalDisplayedConfig.eyebrows = verbalLaughterAppearance.eyebrows;
  } else if (isSettlingDown) {
    finalDisplayedConfig.mouth = settleDownAppearance.mouth;
    finalDisplayedConfig.eyebrows = settleDownAppearance.eyebrows;
  }

  return (
    <div
      className="cursor-pointer"
      onClick={handleTickle}
      role="button"
      aria-label="Tickle the character"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleTickle()}
    >
      <svg
        ref={svgRef}
        width="48"
        height="48"
        viewBox="0 0 48 48"
        className={cn(
          "transition-colors duration-300 transition-transform ease-out",
          finalDisplayedConfig.color
        )}
        style={{ transform: `rotate(${shake}deg)` }}
        overflow="visible"
      >
        <path
          d="M24 4 L8 10 L8 22 Q8 38 24 44 Q40 38 40 22 L40 10 Z"
          className={cn(
            "transition-colors duration-300",
            finalDisplayedConfig.fillColor
          )}
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <g
          transform={`translate(0, ${animationsPaused ? 0 : eyebrowOffset + eyebrowFollowOffset})`}
          className="transition-transform duration-200 ease-out"
        >
          <path
            d={finalDisplayedConfig.eyebrows.left}
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            className="transition-all duration-300"
          />
          <path
            d={finalDisplayedConfig.eyebrows.right}
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            className="transition-all duration-300"
          />
        </g>
        {(tickleLevel === 2 && outrightLaughterAppearance.eyesClosedLaugh) ||
        (tickleLevel === 3 && verbalLaughterAppearance.eyesClosedLaugh) ? (
          <>
            <path
              d={`M 16 ${eyeBaseY - 1} Q 18 ${eyeBaseY + 1.5} 20 ${eyeBaseY - 1}`}
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
            <path
              d={`M 28 ${eyeBaseY - 1} Q 30 ${eyeBaseY + 1.5} 32 ${eyeBaseY - 1}`}
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
          </>
        ) : tickleLevel === 1 && littleTickleAppearance.eyesSquint ? (
          <>
            <path
              d={`M 15 ${eyeBaseY + eyePosition.y} L 21 ${eyeBaseY + eyePosition.y + 0.5}`}
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d={`M 27 ${eyeBaseY + eyePosition.y + 0.5} L 33 ${eyeBaseY + eyePosition.y}`}
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </>
        ) : isBlinking && !animationsPaused ? (
          <>
            <path
              d={`M 16 ${eyeBaseY + eyePosition.y} L 20 ${eyeBaseY + eyePosition.y}`}
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d={`M 28 ${eyeBaseY + eyePosition.y} L 32 ${eyeBaseY + eyePosition.y}`}
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </>
        ) : (
          <>
            <circle
              cx={18 + eyePosition.x}
              cy={eyeBaseY + eyePosition.y}
              r="1.5"
              fill="currentColor"
              className="transition-transform duration-100 ease-out"
            />
            <circle
              cx={30 + eyePosition.x}
              cy={eyeBaseY + eyePosition.y}
              r="1.5"
              fill="currentColor"
              className="transition-transform duration-100 ease-out"
            />
          </>
        )}
        <g
          transform={`translate(0, ${animationsPaused ? 0 : mouthOffset + mouthFollowOffset})`}
          className="transition-transform duration-200 ease-out"
        >
          <path
            d={finalDisplayedConfig.mouth}
            stroke="currentColor"
            strokeWidth="1.5"
            className={cn(
              "transition-all duration-300",
              tickleLevel >= 2
                ? finalDisplayedConfig.fillColor
                : "fill-transparent"
            )}
          />
        </g>
      </svg>
    </div>
  );
};

// --- Main Component ---
interface SmartPasswordInputProps {
  onPasswordSubmit?: (password: string) => void;
  activeRequirements?: PasswordRule[];
  isPending?: boolean;
  disallowedPassword?: string;
}

export default function SmartPasswordInput({
  onPasswordSubmit,
  activeRequirements = [],
  isPending = false,
  disallowedPassword,
}: SmartPasswordInputProps) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tickleLevel, setTickleLevel] = useState<TickleLevel>(0);
  const intl = useIntl();

  const localizedRuleTexts: Record<string, string> = useMemo(
    () => ({
      require_length: intl.formatMessage({
        id: "smartPassword.rule.longEnough",
      }),
      require_lowercase: intl.formatMessage({
        id: "smartPassword.rule.lowercase",
      }),
      require_uppercase: intl.formatMessage({
        id: "smartPassword.rule.uppercase",
      }),
      require_numbers: intl.formatMessage({ id: "smartPassword.rule.number" }),
      require_symbols: intl.formatMessage({
        id: "smartPassword.rule.specialCharacter",
      }),
    }),
    [intl]
  );

  const activeRules = useMemo(() => {
    const lengthRule = ALL_POSSIBLE_RULES.find(
      (rule) => rule.id === "require_length"
    )!;
    const otherRules = ALL_POSSIBLE_RULES.filter(
      (rule) =>
        rule.id !== "require_length" &&
        activeRequirements.includes(rule.id as PasswordRule)
    );
    return [lengthRule, ...otherRules];
  }, [activeRequirements]);

  const strength = useMemo(
    () => calculatePasswordStrength(password, activeRules),
    [password, activeRules]
  );

  const isDisallowedPassword = useMemo(
    () =>
      !!disallowedPassword &&
      password.length > 0 &&
      password === disallowedPassword,
    [password, disallowedPassword]
  );

  const allConfiguredRulesMet = useMemo(() => {
    if (activeRules.length === 0) return true;
    return activeRules.every((rule) => strength.metRules[rule.id]);
  }, [strength.metRules, activeRules]);

  const gridColsClass = useMemo(() => {
    const count = activeRules.length;
    if (count <= 0) return "sm:grid-cols-1";
    if (count === 1) return "sm:grid-cols-1";
    if (count === 2) return "sm:grid-cols-2";
    if (count === 3) return "sm:grid-cols-3";
    if (count === 4) return "sm:grid-cols-4";
    return "sm:grid-cols-5";
  }, [activeRules.length]);

  const feedbackMessage = useMemo(() => {
    if (tickleLevel === 3) {
      return intl.formatMessage({ id: "smartPassword.feedback.tickle" });
    }
    const feedbackMap: Record<string, string> = {
      none: intl.formatMessage({ id: "smartPassword.feedback.none" }),
      weak: intl.formatMessage({ id: "smartPassword.feedback.weak" }),
      fair: intl.formatMessage({ id: "smartPassword.feedback.fair" }),
      good: intl.formatMessage({ id: "smartPassword.feedback.good" }),
      strong: intl.formatMessage({ id: "smartPassword.feedback.strong" }),
      excellent: intl.formatMessage({ id: "smartPassword.feedback.excellent" }),
    };
    return feedbackMap[strength.level] || strength.feedback;
  }, [tickleLevel, strength.level, strength.feedback, intl]);

  return (
    <div>
      <div className="space-y-1">
        <Label htmlFor="new-password" className="text-base-foreground">
          {intl.formatMessage({ id: "smartPassword.label" })}
        </Label>
        <div className="relative">
          <Input
            id="new-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={intl.formatMessage({
              id: "smartPassword.placeholder",
            })}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={
              showPassword
                ? intl.formatMessage({ id: "smartPassword.hidePassword" })
                : intl.formatMessage({ id: "smartPassword.showPassword" })
            }
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border bg-gray-50 p-4 dark:bg-gray-800 dark:border-gray-700/60 mt-[16px] mb-[24px]">
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0 w-12 h-12 flex items-center justify-center">
            {strength.level === "excellent" && <Confetti />}
            <div className="relative z-10">
              <SecurityGuardCharacter
                level={strength.level}
                tickleLevel={tickleLevel}
                setTickleLevel={setTickleLevel}
              />
            </div>
          </div>
          <div className="w-full space-y-2">
            <p className="text-sm font-medium text-base-foreground select-none">
              {feedbackMessage}
            </p>
            <StrengthMeter level={strength.level} />
          </div>
        </div>
        {activeRules.length > 0 && (
          <div
            className={cn(
              "flex flex-wrap gap-x-3 gap-y-2 justify-center",
              gridColsClass
            )}
          >
            {activeRules.map((rule) => (
              <Requirement
                key={rule.id}
                met={!!strength.metRules[rule.id]}
                level={strength.level}
              >
                {localizedRuleTexts[rule.id] || rule.text}
              </Requirement>
            ))}
          </div>
        )}
      </div>

      {isDisallowedPassword && (
        <p className="text-destructive text-sm mb-2">
          {intl.formatMessage({
            id: "smartPassword.error.sameAsTemporary",
          })}
        </p>
      )}

      <Button
        className="w-full"
        disabled={!allConfiguredRulesMet || isPending || isDisallowedPassword}
        onClick={() => onPasswordSubmit?.(password)}
      >
        {intl.formatMessage({ id: "smartPassword.continue" })}
      </Button>
    </div>
  );
}
